import { useNavigate } from 'react-router-dom';
import { Container, Stack, Tooltip, Typography } from '@mui/material';
import { ContentPasteOutlined, EditOutlined, MoreVert, People } from '@mui/icons-material';
import { generateRoutePath, RouteEnum, useGotoRoute } from 'App';
import {
  ActionMenu,
  BreadCrumbs,
  getCreatePath,
  getEditPath,
  getManageUsersPath,
  IconsNew,
  Loader,
  MenuItem,
  SimpleList,
  useCourse,
  useExams,
  usePagination,
  usePartner,
  useResponsive,
} from '@shared';
import * as S from 'shared/styles/SimpleList.styled';
import { CellNewRed } from './List.styled';

type TypeProps = {
  partner: string;
  course: string;
  rootPath: RouteEnum;
};

export const List = ({ partner, course, rootPath }: TypeProps) => {
  const isMobile = useResponsive({ query: 'down', key: 'sm' });

  const coursePath = generateRoutePath({
    name: RouteEnum.Courses,
    params: { partner },
  });

  const [page, setPage] = usePagination();
  const navigate = useNavigate();

  const { name: partnerName, enableSelfService } = usePartner({ partner });
  const courseName = useCourse({ partner, course });
  const { data, isLoading, isFetching, isError } = useExams({ partner, course, page });
  const goToRoute = useGotoRoute(RouteEnum.ExamSessions);
  const breadcrumbsNameMap = [
    {
      path: coursePath,
      name: partnerName,
    },
    {
      path: rootPath,
      name: courseName,
    },
  ];

  const settingsItems: MenuItem[] = [
    {
      label: 'Edit course',
      icon: EditOutlined,
      disabled: !enableSelfService,
      onClick: () => navigate(getEditPath(coursePath, course)),
    },
    {
      label: 'Manage users',
      icon: People,
      onClick: () => navigate(getManageUsersPath(coursePath, course)),
    },
    {
      label: 'Add exam',
      icon: ContentPasteOutlined,
      disabled: !enableSelfService,
      onClick: () => navigate(getCreatePath(rootPath)),
    },
  ];

  if (isLoading) {
    return <Loader />;
  }
  if (isError) {
    return null;
  }

  return (
    <Container component='article'>
      <BreadCrumbs breadcrumbNameMap={breadcrumbsNameMap} />
      <SimpleList
        title={courseName}
        subtitle='List of available exams'
        settingsItems={settingsItems}
        rows={data.exams}
        isLoading={isFetching}
        onRowClick={goToRoute}
        entityName={'exam'}
        renderElems={({ completedExamCount, expired, isRecordOnly, name, uuid }) => (
          <S.Container isMobile={isMobile}>
            <Stack direction='row' alignItems='center'>
              {expired && (
                <Tooltip title='Expired Exam' sx={{ mr: 1 }} arrow>
                  <CellNewRed>
                    <Typography variant='iconCell'>Expired exam</Typography>
                  </CellNewRed>
                </Tooltip>
              )}
              <Typography variant='tableCell'>{name}</Typography>
            </Stack>
            <S.Box isMobile={isMobile}>
              <IconsNew
                recordOnly={isRecordOnly}
                completedExamCount={completedExamCount}
                titles={['', '', 'Sessions']}
              />
              {enableSelfService && (
                <ActionMenu
                  icon={MoreVert}
                  items={[
                    {
                      label: 'Edit exam',
                      icon: EditOutlined,
                      onClick: () => navigate(getEditPath(rootPath, uuid)),
                    },
                  ]}
                />
              )}
            </S.Box>
          </S.Container>
        )}
        pagination={data.pagination.totalPages > 1}
        totalPages={data.pagination.totalPages}
        onPageChange={setPage}
      />
    </Container>
  );
};
